import { buildPayloadFromFields } from '@folio/components/forms/form-builder/form-fields-builders';
import { Button, Card, CardContent, FormBuilder, getResolver, toast } from '@folio/components';
import dayjs from 'dayjs';
import {
  type ClaimDto,
  type Metadata,
  toKeyValueFields,
  useClaimsControllerCreate,
} from 'korint-sdk';
import type { Field } from 'korint-sdk/react';
import { Building2, CalendarDays, PlusIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type AssetDto } from 'korint-sdk';
import { useState } from 'react';
import { AssetClaimItem } from './AssetClaimItem';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import {
  getClaimsReferenceDate,
  isHistoricalClaim as isClaimHistorical,
} from '@folio/utils/claims';
import { Group, Stack } from '@folio/components/ui/flex';
import { Title } from '@folio/components/ui/text';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

interface AssetClaimsCategoryProps {
  assetsById: Record<string, AssetDto>;
  category: string;
  label: string;
  categoryClaims: ClaimDto[];
  hasResponsibility?: boolean;
  claimsMetadata?: Record<string, string>;
  refetchClaims: () => Promise<{ data: ClaimDto[] }>;
  fieldsConfig?: Field[];
}

export function AssetClaimsCategory({
  assetsById,
  category,
  label,
  hasResponsibility,
  claimsMetadata,
  categoryClaims,
  refetchClaims,
  fieldsConfig = [],
}: AssetClaimsCategoryProps) {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const { policy } = usePolicyOnDashboard();

  const assets = Object.values(assetsById);

  const { mutateAsync: createClaim, isPending: isCreateClaimLoading } = useClaimsControllerCreate({
    mutation: {
      onError: () => {
        toast({
          description: t('assetClaims.createError'),
        });
      },
    },
  });

  const toDate = getClaimsReferenceDate(policy.startedAt);

  const isPolicyInNewBusiness = policy.status === 'POLICY_CREATED';

  const assetsCreatedOnCurrentBranch = assets.filter((asset: AssetDto) => asset.createdOnBranch);
  const isViewReadonly = !isPolicyInNewBusiness || assetsCreatedOnCurrentBranch.length === 0;

  const assetSelectField: Field = {
    name: 'assetId',
    type: 'select',
    required: true,
    icon: Building2,
    options: assetsCreatedOnCurrentBranch.map((asset: AssetDto) => ({
      value: asset.id,
      label: asset.name ?? t('assetClaims.assetFallbackName'),
    })),
  };

  const occurredAtField: Field = {
    name: 'occurredAt',
    type: 'date',
    required: true,
    icon: CalendarDays,
    min: dayjs().subtract(36, 'month').format('YYYY-MM-DD'),
    max: toDate.format('YYYY-MM-DD'),
  };

  const allFields: Field[] = [assetSelectField, occurredAtField, ...fieldsConfig];

  const form = useForm({
    resolver: getResolver(allFields),
    mode: 'onTouched',
  });

  const handleSubmit = form.handleSubmit(async data => {
    const payload = buildPayloadFromFields(allFields, data) as {
      assetId?: string;
      occurredAt?: string;
      customFields?: Array<{ key: string; value: string | number | boolean }>;
    };
    const { assetId, occurredAt, customFields = [] } = payload;

    if (!assetId || !occurredAt) {
      return;
    }

    await createClaim({
      data: {
        category,
        externalId: assetId as string,
        metadata: claimsMetadata && (toKeyValueFields(claimsMetadata) as Metadata[]),
        createdAt: new Date().toISOString(),
        occurredAt: dayjs(occurredAt as string)
          .utc()
          .toISOString(),
        customFields,
      },
    });

    await refetchClaims();
    setShowForm(false);
    form.reset({
      assetId: '',
      occurredAt: toDate.format('YYYY-MM-DD'),
    });
  });

  return (
    <>
      <Title
        order={3}
        data-name={category}
        className="text-md flex items-center justify-between py-5 font-medium"
      >
        {label}

        {!isViewReadonly && !showForm && (
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={() => setShowForm(true)}
            leftIcon={<PlusIcon className="h-4" />}
          >
            {t('assetClaims.addClaim')}
          </Button>
        )}
      </Title>
      <Stack>
        {categoryClaims?.length ? (
          <div className="divide-y border rounded-md">
            {categoryClaims?.map(claim => {
              const isHistoricalClaim = isClaimHistorical(claim, toDate);
              const asset = claim.externalId ? assetsById[claim.externalId] : undefined;
              const isClaimReadonly = !isPolicyInNewBusiness || !asset?.createdOnBranch;

              const assetName = asset?.name ?? t('assetClaims.assetFallbackName');

              return (
                <AssetClaimItem
                  key={claim.id}
                  claim={claim}
                  assetName={assetName}
                  fieldsConfig={fieldsConfig}
                  hasResponsibility={hasResponsibility}
                  toDate={toDate.toDate()}
                  isHistoricalClaim={isHistoricalClaim}
                  isReadonly={isClaimReadonly}
                  refetchClaims={refetchClaims}
                />
              );
            })}
          </div>
        ) : null}
        {!isViewReadonly && showForm && (
          <Card>
            <CardContent className="flex flex-col gap-4">
              <FormBuilder fields={allFields} form={form} />
              <Group align="stretch" justify="end">
                <Button
                  onClick={handleSubmit}
                  disabled={!form.formState.isValid || isCreateClaimLoading}
                  loading={isCreateClaimLoading}
                >
                  {t('assetClaims.createClaim')}
                </Button>
              </Group>
            </CardContent>
          </Card>
        )}
      </Stack>
    </>
  );
}
